import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import {
  UNIDAD_MEDIDA_ERROR_MSG,
  buildCreateUnidadMedidaRequest,
  buildUpdateUnidadMedidaRequest,
  isUnidadMedidaAltaRoute,
  normalizeUnidadMedidaCodigo,
  validateUnidadMedidaForm,
} from '../../utils/unidadMedida';

const UnidadMedidaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { idUnidadMedida } = useParams<{ idUnidadMedida: string }>();
  const isNew = isUnidadMedidaAltaRoute(idUnidadMedida);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [idField, setIdField] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const load = useCallback(async () => {
    if (isNew || !idUnidadMedida) return;
    const id = decodeURIComponent(idUnidadMedida);
    try {
      setLoading(true);
      const u = await catalogosService.getUnidadMedida(id);
      setIdField(u.idUnidadMedida);
      setDescripcion(u.descripcion || '');
    } catch (e) {
      enqueueSnackbar('Error al cargar la unidad de medida', { variant: 'error' });
      navigate('/inventario/unidades-medida');
    } finally {
      setLoading(false);
    }
  }, [idUnidadMedida, isNew, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isNew) {
      setIdField('');
      setDescripcion('');
      return;
    }
    load();
  }, [isNew, idUnidadMedida, load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateUnidadMedidaForm({
      isNew,
      codigo: idField,
      nombre: descripcion,
    });
    if (error) {
      enqueueSnackbar(UNIDAD_MEDIDA_ERROR_MSG[error], { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      if (isNew) {
        await catalogosService.createUnidadMedida(
          buildCreateUnidadMedidaRequest(idField, descripcion)
        );
      } else {
        const body = buildUpdateUnidadMedidaRequest(idField, descripcion);
        await catalogosService.updateUnidadMedida(body.idUnidadMedida, body);
      }
      enqueueSnackbar('Guardado correctamente', { variant: 'success' });
      navigate('/inventario/unidades-medida');
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : null) ||
        'Error al guardar';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/inventario/unidades-medida')}
              sx={{ textTransform: 'none' }}
            >
              Unidades de medida
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isNew ? 'Nueva unidad de medida' : 'Editar unidad de medida'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código"
                  value={idField}
                  onChange={(e) => setIdField(normalizeUnidadMedidaCodigo(e.target.value))}
                  fullWidth
                  required
                  disabled={!isNew}
                  inputProps={{ maxLength: 20 }}
                  helperText={isNew ? 'Máx. 20 caracteres' : 'El código no se puede modificar'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nombre"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ maxLength: 20 }}
                  helperText="Máx. 20 caracteres"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/inventario/unidades-medida')}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none' }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UnidadMedidaForm;
